import moment from 'moment';
import { GA4ChartData, GA4ReportData, GA4ChartEntry } from '@hydra/types/traffic';

// Used to append on the qs `state` when sent/received to/from google auth route
// so that we differentiate between GA4 and UA
export const SUFIX_STATE_GA_4 = '_IS_GA4_TRUE';

export const CHANNELS = ['Direct', 'Organic Search', 'Paid Social', 'Organic Social', 'Organic Video', 'Email', 'Affiliates', 'Referral', 'Paid Search', 'Video', 'Display', 'Paid Other', 'Organic Shopping', 'Unassigned'];

export const START_END_DATES = {
  startDate: { substractDays: 30 },
  endDate: { substractDays: 1 }
};

// 30 days ago
const START_DATE = `${START_END_DATES.startDate.substractDays}daysAgo`;
// yesterday
const END_DATE = `${START_END_DATES.endDate.substractDays}daysAgo`;
const DATE_RANGES = [{ startDate: START_DATE, endDate: END_DATE }];

// Chart 01
export const ACQUISITION_OVERVIEW_REPORT = {
  dateRanges: DATE_RANGES,
  dimensions: [{ name: 'date' }],
  metrics: [{ name: 'activeUsers' }, { name: 'newUsers' }],
  orderBys: [{ dimension: { orderType: 'NUMERIC', dimensionName: 'date' }, desc: false }],
  metricAggregations: ['TOTAL']
};

// Chart 02
export const AVERAGE_ENGAGEMENT_TIME_REPORT = {
  dateRanges: DATE_RANGES,
  dimensions: [{ name: 'date' }],
  metrics: [{ name: 'date', expression: 'userEngagementDuration/activeUsers' }],
  orderBys: [{ dimension: { orderType: 'NUMERIC', dimensionName: 'date' }, desc: false }],
  metricAggregations: ['TOTAL']
};

// Chart 03
export const ACQUISITION_CHANNEL_REPORT = {
  dateRanges: DATE_RANGES,
  dimensions: [{ name: 'firstUserDefaultChannelGroup' }],
  orderBys: [{ metric: { metricName: 'newUsers' }, desc: true }],
  metrics: [{ name: 'newUsers' }],
  metricAggregations: ['TOTAL']
};

// Chart 04
export const TOP_PAGES_REPORT = (limit: number = 10) => {
  return {
    dateRanges: DATE_RANGES,
    dimensions: [{ name: 'unifiedPagePathScreen' }],
    metrics: [{ name: 'screenPageViews' }, { name: 'activeUsers' }],
    orderBys: [{ metric: { metricName: 'screenPageViews' }, desc: true }],
    metricAggregations: ['TOTAL'],
    limit
  };
};

// Chart 05
export const PAGES_OVER_TIME_REPORT = (pages: string[]) => {
  const pagesFilter = pages && pages.length
    ? {
        dimensionFilter: {
          filter: {
            inListFilter: {
              caseSensitive: true,
              values: pages
            },
            fieldName: 'unifiedPagePathScreen'
          }
        }
      }
    : {};

  return {
    dateRanges: DATE_RANGES,
    dimensions: [{ name: 'unifiedPagePathScreen' }, { name: 'date' }],
    metrics: [{ name: 'screenPageViews' }],
    orderBys: [{ dimension: { orderType: 'NUMERIC', dimensionName: 'date' }, desc: false }],
    ...pagesFilter,
    metricAggregations: ['TOTAL']
  };
};

// Chart 06
export const EVENTS_REPORT = {
  dateRanges: DATE_RANGES,
  dimensions: [{ name: 'eventName' }],
  metrics: [{ name: 'eventCount' }, { name: 'activeUsers' }],
  orderBys: [{ metric: { metricName: 'eventCount' }, desc: true }],
  metricAggregations: ['TOTAL']
};

// Chart 07
export const USERS_BY_DEVICE_TYPE_REPORT = {
  dateRanges: DATE_RANGES,
  dimensions: [{ name: 'deviceCategory' }],
  metrics: [{ name: 'activeUsers' }],
  orderBys: [{ metric: { metricName: 'activeUsers' }, desc: true }],
  metricAggregations: ['TOTAL']
};

// Chart 08
export const USERS_BY_BROWSER_TYPE_REPORT = {
  dateRanges: DATE_RANGES,
  dimensions: [{ name: 'browser' }],
  metrics: [{ name: 'activeUsers' }],
  orderBys: [{ metric: { metricName: 'activeUsers' }, desc: true }],
  metricAggregations: ['TOTAL']
};

// Chart 09: Traffic Section: Traffic from Search
export const TRAFFIC_FROM_SEARCH = {
  dimensions: [{ name: 'sessionMedium' }, { name: 'sessionSource' }],
  metrics: [{ name: 'totalUsers' }],
  dateRanges: DATE_RANGES,
  dimensionFilter: {
    filter: {
      stringFilter: {
        matchType: 'EXACT',
        value: 'organic',
        caseSensitive: true
      },
      fieldName: 'sessionMedium'
    }
  },
  limit: 20,
  orderBys: [{ metric: { metricName: 'totalUsers' }, desc: true }],
  keepEmptyRows: true,
  metricAggregations: ['TOTAL']
};

// Chart 10: Traffic Section: Traffic from Social
export const TRAFFIC_FROM_SOCIAL = {
  dimensions: [{ name: 'sessionDefaultChannelGroup' }, { name: 'sessionMedium' }, { name: 'sessionSource' }],
  metrics: [{ name: 'totalUsers' }],
  dateRanges: [{ startDate: '30daysAgo', endDate: 'yesterday' }],
  dimensionFilter: {
    filter: {
      stringFilter: { matchType: 'EXACT', value: 'Organic Social', caseSensitive: true },
      fieldName: 'sessionDefaultChannelGroup'
    }
  },
  limit: 20,
  orderBys: [{ metric: { metricName: 'totalUsers' }, desc: true }],
  metricAggregations: ['TOTAL']
};

// Chart 11: Traffic Section: Traffic from Backlinks
export const TRAFFIC_FROM_BACKLINKS = {
  dimensions: [{ name: 'sessionDefaultChannelGroup' }, { name: 'sessionSource' }],
  metrics: [{ name: 'totalUsers' }],
  dateRanges: [{ startDate: '30daysAgo', endDate: 'yesterday' }],
  dimensionFilter: {
    filter: {
      stringFilter: { matchType: 'EXACT', value: 'Referral', caseSensitive: true },
      fieldName: 'sessionDefaultChannelGroup'
    }
  },
  limit: 20,
  orderBys: [{ metric: { metricName: 'totalUsers' }, desc: true }],
  metricAggregations: ['TOTAL']
};

export const PAGEVIEWS_PER_URL_REPORT = (escapedUrl: string) => {
  return {
    dateRanges: DATE_RANGES,
    dimensions: [{ name: 'date' }, { name: 'unifiedPagePathScreen' }],
    metrics: [{ name: 'screenPageViews' }],
    dimensionFilter: {
      filter: {
        fieldName: 'unifiedPagePathScreen',
        stringFilter: { matchType: 'EXACT', value: escapedUrl, caseSensitive: true }
      }
    },
    orderBys: [{ dimension: { orderType: 'NUMERIC', dimensionName: 'date' }, desc: false }],
    metricAggregations: ['TOTAL']
  };
};

export const AVERAGE_ENGAGEMENT_TIME_PER_URL = (escapedUrl: string) => {
  return {
    dateRanges: DATE_RANGES,
    dimensions: [{ name: 'unifiedPagePathScreen' }],
    metrics: [{ expression: 'userEngagementDuration/activeUsers', name: 'userEngagementDurationPerUser' }],
    dimensionFilter: {
      filter: {
        fieldName: 'unifiedPagePathScreen',
        stringFilter: { matchType: 'EXACT', value: escapedUrl, caseSensitive: true }
      }
    }
  };
};

export const BOUNCE_RATE_PER_URL_REPORT = (escapedUrl: string) => {
  return {
    dateRanges: DATE_RANGES,
    dimensions: [{ name: 'unifiedPagePathScreen' }],
    metrics: [{ name: 'bounceRate' }],
    dimensionFilter: {
      filter: {
        fieldName: 'unifiedPagePathScreen',
        stringFilter: { matchType: 'EXACT', value: escapedUrl, caseSensitive: true }
      }
    }
  };
};

export const COUNTRIES_MAP_PER_URL_REPORT = (escapedUrl: string) => {
  return {
    dateRanges: DATE_RANGES,
    dimensions: [{ name: 'country' }, { name: 'countryId' }, { name: 'unifiedPagePathScreen' }],
    metrics: [{ name: 'activeUsers' }],
    dimensionFilter: {
      filter: {
        fieldName: 'unifiedPagePathScreen',
        stringFilter: { matchType: 'EXACT', value: escapedUrl, caseSensitive: true }
      }
    },
    orderBys: [{ metric: { metricName: 'activeUsers' }, desc: true }],
    limit: 20
  };
};

export const SOURCE_SESSIONS_PER_URL_REPORT = (escapedUrl: string) => {
  return {
    dateRanges: DATE_RANGES,
    dimensions: [{ name: 'unifiedPagePathScreen' }, { name: 'sessionSource' }],
    metrics: [{ name: 'activeUsers' }],
    dimensionFilter: {
      filter: {
        fieldName: 'unifiedPagePathScreen',
        stringFilter: { matchType: 'EXACT', value: escapedUrl, caseSensitive: true }
      }
    }
  };
};

export const MEDIUM_SESSIONS_PER_URL_REPORT = (escapedUrl: string) => {
  return {
    dateRanges: DATE_RANGES,
    dimensions: [{ name: 'unifiedPagePathScreen' }, { name: 'sessionMedium' }],
    metrics: [{ name: 'activeUsers' }],
    dimensionFilter: {
      filter: {
        fieldName: 'unifiedPagePathScreen',
        stringFilter: { matchType: 'EXACT', value: escapedUrl, caseSensitive: true }
      }
    }
  };
};

export const DEVICE_CATEGORY_SESSIONS_PER_URL_REPORT = (escapedUrl: string) => {
  return {
    dateRanges: DATE_RANGES,
    dimensions: [{ name: 'landingPagePlusQueryString' }, { name: 'deviceCategory' }],
    metrics: [{ name: 'activeUsers' }],
    dimensionFilter: {
      filter: {
        fieldName: 'landingPagePlusQueryString',
        stringFilter: { matchType: 'EXACT', value: escapedUrl, caseSensitive: true }
      }
    }
  };
};

export const PAGE_REFERRER_PER_URL_REPORT = (escapedUrl: string) => {
  return {
    dateRanges: DATE_RANGES,
    dimensions: [{ name: 'unifiedPagePathScreen' }, { name: 'pageReferrer' }],
    metrics: [{ name: 'screenPageViews' }],
    dimensionFilter: {
      filter: {
        fieldName: 'unifiedPagePathScreen',
        stringFilter: { matchType: 'EXACT', value: escapedUrl, caseSensitive: true }
      }
    },
    orderBys: [{ metric: { metricName: 'screenPageViews' }, desc: true }],
    limit: 20
  };
};

export const REPORTS = [
  ACQUISITION_OVERVIEW_REPORT,
  AVERAGE_ENGAGEMENT_TIME_REPORT,
  ACQUISITION_CHANNEL_REPORT,
  TOP_PAGES_REPORT(),
  PAGES_OVER_TIME_REPORT([]),
  EVENTS_REPORT,
  USERS_BY_DEVICE_TYPE_REPORT,
  USERS_BY_BROWSER_TYPE_REPORT,
  TRAFFIC_FROM_SEARCH,
  TRAFFIC_FROM_SOCIAL,
  TRAFFIC_FROM_BACKLINKS
];

export const ON_PAGE_ADVISOR_REPORTS = [
  PAGEVIEWS_PER_URL_REPORT(''),
  AVERAGE_ENGAGEMENT_TIME_PER_URL(''),
  BOUNCE_RATE_PER_URL_REPORT('')
];

export function populateMissingValues (data: GA4ChartData, startDate: string, endDate: string) {
  try {
    const metricsValuesCount = data.metricHeaders.length;
    const existingDates = data.rows.map(item => moment(item.dimensionValues[0].value, 'YYYYMMDD'));
    const start = moment(startDate, 'YYYYMMDD');
    const end = moment(endDate, 'YYYYMMDD');

    const result = [];
    // https://github.com/eslint/eslint/issues/6984
    // eslint-disable-next-line no-unmodified-loop-condition
    for (let currentDate = moment(start); currentDate <= end; currentDate.add(1, 'days')) {
      const index = existingDates.findIndex(date => date.isSame(currentDate, 'day'));
      if (index >= 0) {
        result.push(data.rows[index]);
      } else {
        result.push({
          dimensionValues: [{ value: currentDate.format('YYYYMMDD') }],
          metricValues: Array(metricsValuesCount).fill({ value: '0' })
        });
      }
    }

    data.rows = result;
    return data;
  } catch (error) {
    // ignore error
    return data;
  }
}

export function populateMissingPageViewsOverTimeValues (data: GA4ReportData, startDate: string, endDate: string) {
  try {
    const pages = data.top5PagesReport.rows.map(row => row.dimensionValues[0].value);
    const result: GA4ChartEntry[] = [];
    pages.forEach(page => {
      const start = moment(startDate, 'YYYYMMDD');
      const end = moment(endDate, 'YYYYMMDD');
      // https://github.com/eslint/eslint/issues/6984
      // eslint-disable-next-line no-unmodified-loop-condition
      for (let currentDate = moment(start); currentDate <= end; currentDate.add(1, 'days')) {
        const currentDayFormatted = currentDate.format('YYYYMMDD');
        const index = data.pagesOverTimeReport.rows.findIndex(row => row.dimensionValues[0].value === page && row.dimensionValues[1].value === currentDayFormatted);
        if (index >= 0) {
          result.push(data.pagesOverTimeReport.rows[index]);
        } else {
          result.push({ dimensionValues: [{ value: page }, { value: currentDayFormatted }], metricValues: [{ value: '0' }] });
        }
      }
    });

    data.pagesOverTimeReport.rows = result;
    return data.pagesOverTimeReport;
  } catch (error) {
    // ignore error
    return data.pagesOverTimeReport;
  }
}

export function getHasData (data: GA4ChartData) {
  return data && data.rows && data.rows.length > 0;
}

export function getPagesFromTopPagesReport (data: GA4ChartData) {
  return data.rows ? data.rows.map(row => row.dimensionValues[0].value) : [];
}

export function transformData (data: GA4ChartData) {
  const dimensions = data.dimensionHeaders.map(header => header.name);
  const metrics = data.metricHeaders.map(header => header.name);

  const transformed = data.rows.map(row => {
    const obj: any = {};
    dimensions.forEach((dimension, i) => {
      obj[`${dimension}Dimension`] = Number.isNaN(parseInt(row.dimensionValues[i].value)) ? row.dimensionValues[i].value : parseInt(row.dimensionValues[i].value);
    });

    metrics.forEach((metric, i) => {
      obj[`${metric}Metric`] = Number.isNaN(parseInt(row.metricValues[i].value)) ? row.metricValues[i].value : parseInt(row.metricValues[i].value);
    });
    return obj;
  });

  const totals = metrics.map((metric, i) => ({
    dataKey: `${metric}Metric`,
    value: parseInt(data.totals ? data.totals[0].metricValues[i].value : 0)
  }));

  return { transformed, totals };
}

export function transformAndSliceForPieChartData (data: GA4ChartData, maxItems = 5) {
  const topItems = data.rows.slice(0, maxItems).map(row => {
    return {
      key: row.dimensionValues[0].value,
      value: parseInt(row.metricValues[0].value || '0')
    };
  });

  const otherValue = data.rows.slice(maxItems).reduce((acc, curr) => {
    return acc + parseInt(curr.metricValues[0].value || '0');
  }, 0);

  const result = [...topItems];
  if (otherValue) {
    result.push({ key: 'Other', value: otherValue });
  }

  return result;
}

export function transformTop5PagesOvertimeData (data: Pick<GA4ReportData, 'top5PagesReport' | 'pagesOverTimeReport'>) {
  const top5PagesData = transformData(data.top5PagesReport).transformed;
  const pagesOverTimeData = transformData(data.pagesOverTimeReport).transformed;

  // Group the pagesOverTimeData by date and page, and sum the metrics
  const groupedData: { [date: string]: { [page: string]: { screenPageViewsMetric: number, screenPageViewsPerUserMetric: number } } } = pagesOverTimeData.reduce((acc, { dateDimension, unifiedPagePathScreenDimension, screenPageViewsMetric, screenPageViewsPerUserMetric }) => {
    const dateGroup = acc[dateDimension] ?? {};
    const pageMetrics = dateGroup[unifiedPagePathScreenDimension] ?? { screenPageViewsMetric: 0, screenPageViewsPerUserMetric: 0 };
    pageMetrics.screenPageViewsMetric += screenPageViewsMetric;
    pageMetrics.screenPageViewsPerUserMetric += screenPageViewsPerUserMetric;
    dateGroup[unifiedPagePathScreenDimension] = pageMetrics;
    acc[dateDimension] = dateGroup;
    return acc;
  }, {});

  // Build the final data array by merging the groupedData with the top5PagesData
  const finalData = Object.entries(groupedData).map(([dateDimension, dateGroup]) => {
    const obj: { [key: string]: number } = { dateDimension: parseInt(dateDimension) };
    top5PagesData.forEach(({ unifiedPagePathScreenDimension }) => {
      obj[unifiedPagePathScreenDimension] = dateGroup[unifiedPagePathScreenDimension]?.screenPageViewsMetric ?? 0;
    });
    return obj;
  });

  const totals = top5PagesData.map(({ unifiedPagePathScreenDimension, screenPageViewsMetric }) => ({
    dataKey: unifiedPagePathScreenDimension,
    value: screenPageViewsMetric
  }));

  return { transformed: finalData, totals };
}

export function transformUserByDeviceData (data: GA4ChartData, valueKey = 'activeUsersMetric') {
  const transformedData = transformData(data).transformed;

  const pieChartData = transformedData.map((item) => ({
    key: item.deviceCategoryDimension,
    value: item[valueKey]
  }));

  return pieChartData;
}

export function transformAndSliceSessionsPerUrlData (data: GA4ChartData, key: string, valueKey: string, maxItems: number = 5) {
  const transformedData = transformData(data).transformed;

  const pieChartData = transformedData.map((item) => ({
    key: item[`${key}Dimension`],
    value: item[`${valueKey}Metric`]
  })).sort((a, b) => b.value - a.value);

  const slicedData = pieChartData.slice(0, maxItems);

  const otherValue = pieChartData.slice(maxItems).reduce((acc, curr) => {
    return acc + parseInt(curr.value);
  }, 0);

  return [
    ...slicedData,
    {
      key: 'Other',
      value: otherValue
    }
  ];
}

interface PageViewsData {
  overallViewRate: number;
  totalScreenPageViews: number;
  viewsOverTime: {
    date: number;
    screenPageViewsMetric: number;
  }[];
}

interface TransformedPagesViewsData extends PageViewsData {
  url: string;
}

export function transformPagesViewsData (data: GA4ReportData) {
  const { transformed: topPagesData, totals: topPagesTotals } = transformData(data.top5PagesReport);
  const pagesOverTimeData = transformData(data.pagesOverTimeReport).transformed;
  const screenPageViewsTotal = topPagesTotals.find(x => x.dataKey === 'screenPageViewsMetric');
  const totalViews = screenPageViewsTotal
    ? screenPageViewsTotal.value
    : parseInt(data.top5PagesReport.totals[0].metricValues[0].value);

  // Group the pagesOverTimeData by page urls
  const groupedData = new Map<string, PageViewsData>();

  pagesOverTimeData.forEach(record => {
    const { unifiedPagePathScreenDimension: pagePath, screenPageViewsMetric, dateDimension: date } = record;
    if (!groupedData.has(pagePath)) {
      groupedData.set(pagePath, { overallViewRate: 0, totalScreenPageViews: 0, viewsOverTime: [] });
    }
    const pageData = groupedData.get(pagePath)!;
    pageData.totalScreenPageViews += screenPageViewsMetric;
    pageData.viewsOverTime.push({ date, screenPageViewsMetric });
  });

  const finalData: TransformedPagesViewsData[] = [];

  for (const [url, pageData] of groupedData.entries()) {
    const page = topPagesData.find(page => page.unifiedPagePathScreenDimension === url);
    pageData.totalScreenPageViews = page ? page.screenPageViewsMetric : pageData.totalScreenPageViews;

    const overallViewRate = Math.round(((pageData.totalScreenPageViews / totalViews) * 100) * 10) / 10;
    finalData.push({ url, ...pageData, overallViewRate });
  }

  const sorted = finalData.sort((a, b) => b.totalScreenPageViews - a.totalScreenPageViews);
  return {
    totalViews,
    data: sorted.map((item, i) => ({ ...item, index: i + 1 }))
  };
}

export function transformEventsData (data: GA4ChartData) {
  const total = parseInt(data.totals[0].metricValues[0].value);

  return {
    data: transformData(data).transformed.map((item, i) => ({ ...item, index: i + 1 })),
    total
  };
}
